import type { ComponentProps } from "react";
import { useTranslation } from "@privacy/translations";
import { WhiteSpace } from "@privacy/ui";
import { useGetRegistrationById, useGetRegistrationForm, useGetRegistrationHistory } from "../../../api/registrations";
import { TenantControllerName } from "../../../shared/ConnectedTenantsList";
import { useCurrentCustomer } from "../../../root/CurrentOrganisationProvider";
import { HistorySource } from "../../../shared/HistorySource";
import type { ConnectedTenantsHistoryValue } from "../../../api/registrations.types";
import { connectedTenantsFromHistory } from "../../../api/registrations.types";
import { DynamicGrid } from "../../../dynamicGrid";
import { DynamicHistory } from "../../../dynamicHistory";
import { TenantIcon3 } from "../../../shared/icons";
import { Drawer } from "../../../shared/Drawer";
import { RegistrationDrawerHeader } from "./RegistrationDrawerHeader";
import { useRegistrationVariant } from "../RegistrationVariantProvider";
import { isSharedRegistrationEvent } from "../../../shared/SharedRegistrationEventEntry";
import { RegistrationCustomEntry } from "../../../shared/RegistrationCustomEntry";

export const RegistrationHistoryDrawer = () => {
  const registrationId = DynamicGrid.useSelectedRowId();
  const { t } = useTranslation();
  const variant = useRegistrationVariant();
  const registrationHistoryQuery = useGetRegistrationHistory(registrationId, variant);
  const registrationByIdQuery = useGetRegistrationById(registrationId, variant);
  const registrationFormQuery = useGetRegistrationForm(variant);
  const managedBy = registrationByIdQuery.data?.organisationEditability?.managedBy;
  const isCustomerEntry = (entry: { tenantId?: string }) => entry.tenantId === managedBy?.id;
  return (
    <DynamicGrid.Drawer>
      {(() => {
        if (!registrationByIdQuery.data) return <Drawer.Loading />;

        return (
          <>
            <RegistrationDrawerHeader
              registration={registrationByIdQuery.data}
              activeTab="history"
              statusIndicator={"hidden"}
            />

            {!registrationHistoryQuery.data ? (
              <Drawer.Loading />
            ) : (
              <DynamicHistory.Entries
                historyEntries={registrationHistoryQuery.data.history}
                renderSource={
                  managedBy
                    ? entry =>
                        !isCustomerEntry(entry) ||
                        (entry.type === "customHistoryEntry" && isSharedRegistrationEvent(entry)) ? null : (
                          <HistorySource name={managedBy.name} />
                        )
                    : undefined
                }
                renderCustomEntry={entry =>
                  entry.customType === "assistantAnswersConfirmed" ? (
                    <DynamicHistory.AssistantAnswersConfirmedEntry
                      createdAt={entry.createdAt}
                      createdBy={entry.createdBy}
                      count={entry.newValue}
                    />
                  ) : entry.customType === "registrationConnectedTenant" ? (
                    <ConnectedTenantsChange
                      {...entry}
                      header={t(`RegistrationDrawer.ConnectedTenant.${variant}.Label`)}
                    />
                  ) : (
                    <RegistrationCustomEntry
                      entry={entry}
                      form={registrationFormQuery.data}
                      responsibleHeader={
                        managedBy && isCustomerEntry(entry)
                          ? t("RegistrationDrawer.Responsible.LibraryResponsible")
                          : undefined
                      }
                    />
                  )
                }
              />
            )}
          </>
        );
      })()}
    </DynamicGrid.Drawer>
  );
};

type ConnectedTenantsChangeProps = Omit<
  ComponentProps<typeof DynamicHistory.BeforeAndAfterEntry>,
  "before" | "after"
> & {
  oldValue: ConnectedTenantsHistoryValue;
  newValue: ConnectedTenantsHistoryValue;
};

const ConnectedTenantsChange = ({ oldValue, newValue, ...entry }: ConnectedTenantsChangeProps) => (
  <DynamicHistory.BeforeAndAfterEntry
    {...entry}
    before={<ConnectedTenantsValue value={oldValue} />}
    after={<ConnectedTenantsValue value={newValue} />}
  />
);

const ConnectedTenantsValue = ({ value }: { value: ConnectedTenantsHistoryValue }) => {
  const variant = useRegistrationVariant();
  const currentCustomer = useCurrentCustomer();
  const tenants = connectedTenantsFromHistory(value);

  if (tenants.length === 0) return <DynamicHistory.EmptyValue />;

  return (
    <DynamicHistory.MultipleValues>
      {tenants.map(tenant => (
        <DynamicHistory.SnapshotValueText key={tenant.value} Icon={TenantIcon3}>
          {tenant.name}
          <WhiteSpace />
          {currentCustomer.customerKind !== "enterprise" &&
            variant === "Controller" &&
            tenant.personalDataController && (
              <TenantControllerName>{tenant.personalDataController.name}</TenantControllerName>
            )}
        </DynamicHistory.SnapshotValueText>
      ))}
    </DynamicHistory.MultipleValues>
  );
};
